import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Lock, ArrowRight, Eye, EyeOff } from 'lucide-react';
import { useAuthContext } from '../contexts/AuthContext';
import { gradientText, gradientButton } from '../theme';
import { supabase } from '../lib/supabase';

function Login() {
  const { t } = useTranslation();
  const { signIn } = useAuthContext();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const validateForm = () => {
    const newErrors: { email?: string; password?: string } = {};

    if (!email) {
      newErrors.email = t('auth.validation.emailRequired');
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = t('auth.validation.emailInvalid');
    }

    if (!password) {
      newErrors.password = t('auth.validation.passwordRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm() || isLoading) return;

    setIsLoading(true);
    setAuthError(null);

    // Récupérer l'ID de la relation en attente et l'URL de redirection pour le logging
    const pendingRelationId = localStorage.getItem('pendingRelationId');
    const redirectPath = localStorage.getItem('redirectAfterAuth');
    
    console.log("[Login] Starting login process:", {
      email,
      pendingRelationId,
      redirectPath
    });

    const { error } = await signIn(email, password);
    
    if (error) {
      // Vérifier si l'erreur indique que l'utilisateur utilise l'authentification Google
      if (error.message?.includes('Invalid login credentials')) {
        // Vérifier si l'utilisateur existe avec ce mail
        const { data } = await supabase.auth.signInWithOtp({
          email,
          options: {
            shouldCreateUser: false
          }
        });
        
        if (data) {
          setAuthError(t('auth.login.googleAccount', { defaultValue: 'Ce compte utilise la connexion avec Google. Veuillez utiliser le bouton "Continuer avec Google".' }));
        } else {
          setAuthError(t('auth.login.invalidCredentials'));
        }
      } else {
        setAuthError(t('auth.login.invalidCredentials'));
      }
      setIsLoading(false);
      return;
    }

    console.log("[Login] Login successful, redirection will be handled by useAuth");
    
    // Force de redirection si nécessaire après un court délai
    setTimeout(() => {
      // Si nous sommes toujours sur la page de connexion, forcer une redirection
      if (window.location.hash === '#/login') {
        console.log("[Login] Still on login page, forcing redirection");
        if (pendingRelationId) {
          window.location.replace(`${window.location.origin}/#/share/${pendingRelationId}`);
        } else if (redirectPath) {
          window.location.replace(`${window.location.origin}/#${redirectPath}`);
        } else {
          window.location.replace(`${window.location.origin}/#/profile`);
        }
      }
    }, 2500);
    
    // La redirection sera gérée par handleRedirectAfterAuth dans useAuth
    setIsLoading(false);
  };

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    setAuthError(null);

    // Vérifier si un pendingRelationId existe déjà
    const pendingRelationId = localStorage.getItem('pendingRelationId');
    console.log("[Login] Starting Google login with pending relation:", pendingRelationId);

    // Stocker le chemin actuel pour la redirection si ce n'est pas déjà fait
    if (!localStorage.getItem('redirectAfterAuth')) {
      const currentPath = window.location.hash.substring(1); // Utiliser hash pour la compatibilité HashRouter
      if (currentPath && currentPath !== '/login' && currentPath !== '/signup') {
        localStorage.setItem('redirectAfterAuth', currentPath);
        console.log("[Login] Stored current path for redirect:", currentPath);
      }
    }

    // S'assurer que nous n'avons pas déjà une redirection en cours
    localStorage.removeItem('redirectionInProgress');

    // Détecter Safari sur iOS
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin + window.location.pathname,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
            // Forcer le mode popup sur Safari iOS pour éviter les problèmes de redirection
            ...(isSafari && isIOS ? { display: 'popup' } : {})
          },
          scopes: 'email profile'
        }
      });
      
      if (error) throw error;
      
      console.log("[Login] Google auth initiated, redirecting to Google...");
    } catch (error) {
      console.error('[Login] Google login error:', error);
      setAuthError(t('auth.login.googleError'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex flex-col justify-center py-8 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-sm">
        <img
          src="/Final Logo 192.png"
          alt="Logo"
          className="mx-auto w-16 h-16 mb-4"
        />
        <h2 className={`text-center text-2xl font-bold ${gradientText} mb-6`}>
          {t('auth.login.title')}
        </h2>
      </div>

      <div className="sm:mx-auto sm:w-full sm:max-w-sm">
        <div className="bg-[#0A0A0A] py-6 px-4 shadow sm:rounded-2xl sm:px-8">
          {authError && (
            <div className="mb-4 p-2.5 rounded-xl bg-red-500/10 border border-red-500/20 text-red-500 text-sm">
              {authError}
            </div>
          )}

          <form className="space-y-4" onSubmit={handleSubmit}>
            <div>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`w-full bg-black border ${
                    errors.email ? 'border-red-500' : 'border-gray-800'
                  } rounded-xl pl-9 pr-3 py-2.5 focus:outline-none focus:border-primary-end text-white text-sm`}
                  placeholder={t('auth.login.email')}
                  disabled={isLoading}
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-xs text-red-500">{errors.email}</p>
              )}
            </div>

            <div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`w-full bg-black border ${
                    errors.password ? 'border-red-500' : 'border-gray-800'
                  } rounded-xl pl-9 pr-9 py-2.5 focus:outline-none focus:border-primary-end text-white text-sm`}
                  placeholder={t('auth.login.password')}
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-xs text-red-500">{errors.password}</p>
              )}
            </div>

            <div className="flex items-center justify-end">
              <Link
                to="/forgot-password"
                className={`text-xs ${gradientText} hover:opacity-80 transition-opacity`}
              >
                {t('auth.login.forgotPassword')}
              </Link>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className={`w-full ${gradientButton} text-white font-medium py-2.5 px-4 rounded-xl flex items-center justify-center gap-2 transition-colors disabled:opacity-50 text-sm`}
            >
              {isLoading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  {t('auth.login.submit')}
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>

          <div className="mt-5">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-800" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-2 bg-[#0A0A0A] text-gray-400">ou</span>
              </div>
            </div>

            <button
              type="button"
              onClick={handleGoogleLogin}
              disabled={isLoading}
              className="mt-4 w-full bg-white hover:bg-gray-50 text-gray-900 font-medium py-2.5 px-4 rounded-xl flex items-center justify-center gap-2 transition-colors disabled:opacity-50 text-sm"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              {t('auth.login.googleButton')}
            </button>
          </div>

          <p className="mt-5 text-center text-xs text-gray-400">
            {t('auth.login.noAccount')}{' '}
            <Link
              to="/signup"
              className={`${gradientText} hover:opacity-80 transition-opacity`}
            >
              {t('auth.login.signupLink')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
export default Login;
